import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * What had focus when the main drawer opened. Besides the hamburger <summary>, the first
   * desktop menu link opens it (Alpine `header` → menu-drawer-open), so closing returns here
   * rather than always to the summary.
   * @type {HTMLElement | null}
   */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    window.addEventListener('menu-drawer-open', () => {
      console.log('menu-drawer-open');
      this.open();
    });

    window.addEventListener('menu-drawer-close', () => {
      console.log('menu-drawer-close');
      this.close();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    console.log('toggle');
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    console.log('event', event);
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    console.log('summary', summary);
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      const active = document.activeElement;
      this.#opener = active instanceof HTMLElement && !this.refs.menuDrawer.contains(active) ? active : null;
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      console.log('details', details);
      details.classList.add('menu-open');

      if (target) {
        console.log('target', target);
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      console.log('drawer', drawer);
      onAnimationEnd(
        drawer || details,
        () => {
          if (details === this.refs.details) {
            this.#trapDrawer();
            this.#focusFirstWhenVisible();
          } else {
            trapFocus(details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Trap focus in the role="dialog" panel rather than the whole <details>: the details also holds
   * the hamburger <summary>, which sits outside the dialog, and isn't focusable itself, so trapping
   * it left focus on the trigger. Optionally moves focus to `target` once the trap is set.
   * @param {HTMLElement | null} [target]
   */
  #trapDrawer(target) {
    trapFocus(this.refs.menuDrawer);
    target?.focus();
  }

  /**
   * onAnimationEnd only waits for animations already running when it's called; on reopen the
   * drawer's transition often hasn't started yet, so the callback fires while the panel is still
   * hidden and there is nothing visible to focus. Retry each frame (~1s) until the panel shows.
   * @param {number} [framesLeft]
   */
  #focusFirstWhenVisible(framesLeft = 60) {
    if (!this.refs.details.classList.contains('menu-open')) return;

    const target = this.#firstFocusable();
    if (target) {
      target.focus();
    } else if (framesLeft > 0) {
      requestAnimationFrame(() => this.#focusFirstWhenVisible(framesLeft - 1));
    }
  }

  /**
   * @returns {HTMLElement | null} The first visible focusable control in the drawer panel
   */
  #firstFocusable() {
    const candidates = this.refs.menuDrawer.querySelectorAll(
      'a[href], button:enabled, summary, input:not([type=hidden]):enabled, select:enabled, [tabindex]:not([tabindex^="-"])'
    );
    return (
      Array.from(candidates).find(
        (el) => el instanceof HTMLElement && !el.closest('[inert]') && (el.checkVisibility?.() ?? true)
      ) ?? null
    );
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // Focus still inside the now-hidden panel (or already dropped to <body>) belongs back
        // on what opened it; anywhere else, the user has moved on and it stays put.
        const active = document.activeElement;
        const focusWasInPanel = !active || active === document.body || details.contains(active);

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          const opener = this.#opener;
          this.#opener = null;
          if (focusWasInPanel) {
            const returnTo = opener?.isConnected && (opener.checkVisibility?.() ?? true) ? opener : summary;
            returnTo.focus();
          }
          // Let the Alpine `header` component (which can open this drawer) drop its open state,
          // or its trigger keeps aria-expanded="true" after an Escape/close-button close.
          window.dispatchEvent(new CustomEvent('menu-drawer-closed'));
        } else {
          this.#trapDrawer(focusWasInPanel ? summary : null);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
